import { VehicleValuationType } from "@/api-schema-enums";
import type { VehicleValuationCostAddition } from "@/common/api/vehicle-valuation-cost-addition/vehicle-valuation-cost-addition.types";
import type { VehicleValuationByUuidResponse } from "@/common/api/vehicle-valuation/use-vehicle-valuation-by-uuid.query";
import type { VehicleValuationConfirmBody } from "@/common/api/vehicle-valuation/use-vehicle-valuation-confirm.mutation";
import { formatNumberInput, isFiniteNumber, roundToDecimalPlaces } from "@/common/helpers/decimal-number";
import {
  isLocalizedNumberInput,
  parseLocalizedNumberInput,
  type LocalizedNumberOptions,
} from "@/common/helpers/number-format";
import { z } from "zod";

type VehicleValuation = VehicleValuationByUuidResponse;

type ConfirmFormFieldPath =
  | "confirmedTradeGross"
  | "confirmedSalesGross"
  | "confirmedWarranty"
  | "confirmedVehiclePreparationCosts"
  | "confirmedMarginGross"
  | "confirmedMarginPercentage"
  | "leaseBuyoutAmount";

type ConfirmedAmountFieldPath = Exclude<ConfirmFormFieldPath, "leaseBuyoutAmount" | "confirmedMarginPercentage">;

interface ConfirmFormValidationRule<TFieldPath extends ConfirmFormFieldPath = ConfirmFormFieldPath> {
  invalidMessageKey: string;
  options: LocalizedNumberOptions;
  path: TFieldPath;
  requiredMessageKey: string;
}

const LEASE_BUYOUT_AMOUNT_VALIDATION_RULE = {
  path: "leaseBuyoutAmount",
  requiredMessageKey: "fab:LeaseBuyoutAmountIsRequired",
  invalidMessageKey: "fab:LeaseBuyoutAmountMustBeANumber",
  options: { allowNegative: false },
} as const satisfies ConfirmFormValidationRule<"leaseBuyoutAmount">;

const CONFIRMED_AMOUNT_VALIDATION_RULES = [
  {
    path: "confirmedTradeGross",
    requiredMessageKey: "masterdata:ConfirmedTradeGrossIsRequired",
    invalidMessageKey: "masterdata:ConfirmedTradeGrossMustBeANumber",
    options: { allowNegative: false },
  },
  {
    path: "confirmedSalesGross",
    requiredMessageKey: "masterdata:ConfirmedSalesGrossIsRequired",
    invalidMessageKey: "masterdata:ConfirmedSalesGrossMustBeANumber",
    options: { allowNegative: false },
  },
  {
    path: "confirmedWarranty",
    requiredMessageKey: "masterdata:ConfirmedWarrantyIsRequired",
    invalidMessageKey: "masterdata:ConfirmedWarrantyMustBeANumber",
    options: { allowNegative: false },
  },
  {
    path: "confirmedVehiclePreparationCosts",
    requiredMessageKey: "masterdata:ConfirmedVehiclePreparationCostsIsRequired",
    invalidMessageKey: "masterdata:ConfirmedVehiclePreparationCostsMustBeANumber",
    options: { allowNegative: false },
  },
  {
    path: "confirmedMarginGross",
    requiredMessageKey: "masterdata:ConfirmedMarginGrossIsRequired",
    invalidMessageKey: "masterdata:ConfirmedMarginGrossMustBeANumber",
    options: { allowNegative: true },
  },
] as const satisfies ReadonlyArray<ConfirmFormValidationRule<ConfirmedAmountFieldPath>>;

const CONFIRMED_MARGIN_PERCENTAGE_VALIDATION_RULE = {
  path: "confirmedMarginPercentage",
  requiredMessageKey: "masterdata:ConfirmedMarginPercentageIsRequired",
  invalidMessageKey: "masterdata:ConfirmedMarginPercentageMustBeANumber",
  options: { allowNegative: true },
} as const satisfies ConfirmFormValidationRule<"confirmedMarginPercentage">;

export const FORM_SET_VALUE_OPTIONS = { shouldValidate: true } as const;
export const DEFAULT_MARGIN_COST_ADDITION_KEY = "DefaultMargin";
export const VEHICLE_PREPARATION_COSTS_COST_ADDITION_KEY = "VehiclePreparationCosts";
export const NUMBER_COMPARISON_EPSILON = 0.0001;

export function getVehicleValuationConfirmFormSchema(t: (key: string) => string) {
  return z
    .object({
      confirmedTradeGross: z.string().trim().optional(),
      confirmedSalesGross: z.string().trim().optional(),
      confirmedWarranty: z.string().trim().optional(),
      confirmedVehiclePreparationCosts: z.string().trim().optional(),
      confirmedMarginGross: z.string().trim().optional(),
      confirmedMarginPercentage: z.string().trim().optional(),
      leaseBuyoutAmount: z.string().trim().optional(),
      note: z.string().optional(),
      valuationType: z.enum(VehicleValuationType),
    })
    .superRefine((values, context) => {
      const validateField = (value: string | undefined, rule: ConfirmFormValidationRule): boolean => {
        if (!value) {
          context.addIssue({
            code: "custom",
            message: t(rule.requiredMessageKey),
            path: [rule.path],
          });

          return false;
        }

        if (!isLocalizedNumberInput(value, rule.options)) {
          context.addIssue({
            code: "custom",
            message: t(rule.invalidMessageKey),
            path: [rule.path],
          });

          return false;
        }

        return true;
      };

      if (values.valuationType === VehicleValuationType.LeasingBuyout) {
        validateField(values.leaseBuyoutAmount, LEASE_BUYOUT_AMOUNT_VALIDATION_RULE);

        return;
      }

      const rules =
        values.valuationType === VehicleValuationType.WholesaleBuyer
          ? CONFIRMED_AMOUNT_VALIDATION_RULES.filter(
              (rule) => rule.path === "confirmedTradeGross" || rule.path === "confirmedSalesGross",
            )
          : CONFIRMED_AMOUNT_VALIDATION_RULES;

      for (const rule of rules) {
        validateField(values[rule.path], rule);
      }

      if (values.valuationType === VehicleValuationType.WholesaleBuyer) {
        const confirmedTradeGross = parseLocalizedNumberInput(values.confirmedTradeGross ?? "", {
          allowNegative: false,
        });
        const confirmedSalesGross = parseLocalizedNumberInput(values.confirmedSalesGross ?? "", {
          allowNegative: false,
        });

        if (
          Number.isFinite(confirmedTradeGross) &&
          Number.isFinite(confirmedSalesGross) &&
          Math.abs(confirmedTradeGross - confirmedSalesGross) > NUMBER_COMPARISON_EPSILON
        ) {
          context.addIssue({
            code: "custom",
            message: t("fab:WholesaleBuyerConfirmedAmountsMustMatch"),
            path: ["confirmedTradeGross"],
          });
        }

        return;
      }

      const hasValidMarginPercentage = validateField(
        values.confirmedMarginPercentage,
        CONFIRMED_MARGIN_PERCENTAGE_VALIDATION_RULE,
      );

      if (!hasValidMarginPercentage) {
        return;
      }

      const confirmedMarginPercentage = parseLocalizedNumberInput(values.confirmedMarginPercentage ?? "", {
        allowNegative: true,
      });

      if (Number.isNaN(confirmedMarginPercentage) || confirmedMarginPercentage < 0 || confirmedMarginPercentage > 100) {
        context.addIssue({
          code: "custom",
          message: t("masterdata:ConfirmedMarginPercentageMustBeBetween0And100"),
          path: ["confirmedMarginPercentage"],
        });
      }
    });
}

export type VehicleValuationConfirmFormSchema = ReturnType<typeof getVehicleValuationConfirmFormSchema>;
export type VehicleValuationConfirmFormValues = z.infer<VehicleValuationConfirmFormSchema>;

export function calculateAdditionalCostsGross(warranty: number, vehiclePreparationCosts: number): number {
  return roundToDecimalPlaces(warranty + vehiclePreparationCosts);
}

export function resolveAutomaticWarranty(valuation: VehicleValuation): number {
  return isFiniteNumber(valuation.warranty) ? valuation.warranty : 0;
}

export function resolveConfirmedWarranty(valuation: VehicleValuation): number {
  return isFiniteNumber(valuation.confirmedWarranty)
    ? valuation.confirmedWarranty
    : resolveAutomaticWarranty(valuation);
}

export function resolveConfirmedAdditionalCostsGross(
  confirmedWarrantyValue?: string,
  confirmedVehiclePreparationCostsValue?: string,
): number | null {
  const confirmedWarranty = parseLocalizedNumberInput(confirmedWarrantyValue ?? "", { allowNegative: false });
  const confirmedVehiclePreparationCosts = parseLocalizedNumberInput(confirmedVehiclePreparationCostsValue ?? "", {
    allowNegative: false,
  });

  if (!Number.isFinite(confirmedWarranty) || !Number.isFinite(confirmedVehiclePreparationCosts)) {
    return null;
  }

  return calculateAdditionalCostsGross(confirmedWarranty, confirmedVehiclePreparationCosts);
}

export function calculateDerivedGrossValues(
  salesGross: number,
  marginPercentage: number,
  additionalCostsGross: number,
): {
  confirmedMarginGross: number;
  confirmedTradeGross: number;
} {
  const confirmedMarginGross = roundToDecimalPlaces((marginPercentage / 100) * salesGross);
  const confirmedTradeGross = roundToDecimalPlaces(salesGross - confirmedMarginGross - additionalCostsGross);

  return {
    confirmedMarginGross,
    confirmedTradeGross,
  };
}

export function calculateValuesFromTradeGross(
  salesGross: number,
  tradeGross: number,
  additionalCostsGross: number,
): {
  confirmedMarginGross: number;
  confirmedMarginPercentage: number;
} {
  const confirmedMarginGross = roundToDecimalPlaces(salesGross - tradeGross - additionalCostsGross);
  const confirmedMarginPercentage =
    salesGross !== 0 ? roundToDecimalPlaces((confirmedMarginGross / salesGross) * 100) : 0;

  return {
    confirmedMarginGross,
    confirmedMarginPercentage,
  };
}

export function calculateValuesFromMarginGross(
  salesGross: number,
  marginGross: number,
  additionalCostsGross: number,
): {
  confirmedTradeGross: number;
  confirmedMarginPercentage: number;
} {
  const confirmedTradeGross = roundToDecimalPlaces(salesGross - marginGross - additionalCostsGross);
  const confirmedMarginPercentage = salesGross !== 0 ? roundToDecimalPlaces((marginGross / salesGross) * 100) : 0;

  return {
    confirmedTradeGross,
    confirmedMarginPercentage,
  };
}

export interface BuildInitialFormValuesOptions {
  managedDefaultMarginPercentage?: number | null;
  managedVehiclePreparationCosts?: number | null;
}

export function resolveAutomaticVehiclePreparationCosts(
  valuation: VehicleValuation,
  managedVehiclePreparationCosts?: number | null,
): number {
  return isFiniteNumber(valuation.vehiclePreparationCosts)
    ? valuation.vehiclePreparationCosts
    : (managedVehiclePreparationCosts ?? 0);
}

export function resolveConfirmedVehiclePreparationCosts(
  valuation: VehicleValuation,
  managedVehiclePreparationCosts?: number | null,
): number {
  return isFiniteNumber(valuation.confirmedVehiclePreparationCosts)
    ? valuation.confirmedVehiclePreparationCosts
    : resolveAutomaticVehiclePreparationCosts(valuation, managedVehiclePreparationCosts);
}

export function buildInitialFormValues(
  valuation: VehicleValuation,
  options: BuildInitialFormValuesOptions = {},
): VehicleValuationConfirmFormValues {
  const confirmedSalesGross = valuation.salesGross;
  const effectiveConfirmedWarranty = resolveAutomaticWarranty(valuation);
  const effectiveMarginPercentage = valuation.marginPercentage ?? options.managedDefaultMarginPercentage;
  const effectiveConfirmedVehiclePreparationCosts = resolveAutomaticVehiclePreparationCosts(
    valuation,
    options.managedVehiclePreparationCosts,
  );
  const additionalCostsGross = calculateAdditionalCostsGross(
    effectiveConfirmedWarranty,
    effectiveConfirmedVehiclePreparationCosts,
  );

  if (isFiniteNumber(confirmedSalesGross) && isFiniteNumber(effectiveMarginPercentage)) {
    const { confirmedMarginGross, confirmedTradeGross } = calculateDerivedGrossValues(
      confirmedSalesGross,
      effectiveMarginPercentage,
      additionalCostsGross,
    );

    return {
      confirmedTradeGross: formatNumberInput(confirmedTradeGross),
      confirmedSalesGross: formatNumberInput(confirmedSalesGross),
      confirmedWarranty: formatNumberInput(effectiveConfirmedWarranty),
      confirmedVehiclePreparationCosts: formatNumberInput(effectiveConfirmedVehiclePreparationCosts),
      confirmedMarginGross: formatNumberInput(confirmedMarginGross),
      confirmedMarginPercentage: formatNumberInput(effectiveMarginPercentage),
      leaseBuyoutAmount: formatNumberInput(valuation.leaseBuyoutAmount),
      note: valuation.note ?? "",
      valuationType:
        (valuation.valuationType as VehicleValuationType | null | undefined) ?? VehicleValuationType.Default,
    };
  }

  return {
    confirmedTradeGross: formatNumberInput(valuation.tradeGross),
    confirmedSalesGross: formatNumberInput(confirmedSalesGross),
    confirmedWarranty: formatNumberInput(effectiveConfirmedWarranty),
    confirmedVehiclePreparationCosts: formatNumberInput(effectiveConfirmedVehiclePreparationCosts),
    confirmedMarginGross: formatNumberInput(valuation.marginGross),
    confirmedMarginPercentage: formatNumberInput(effectiveMarginPercentage),
    leaseBuyoutAmount: formatNumberInput(valuation.leaseBuyoutAmount),
    note: valuation.note ?? "",
    valuationType: (valuation.valuationType as VehicleValuationType | null | undefined) ?? VehicleValuationType.Default,
  };
}

export function resolveManagedCostAdditionValue(
  additions: Array<Pick<VehicleValuationCostAddition, "key" | "valueNumber">> | undefined,
  key: string,
): number | null {
  return (additions ?? []).reduce<number | null>((resolvedValue, addition) => {
    if (addition.key !== key || !isFiniteNumber(addition.valueNumber)) {
      return resolvedValue;
    }

    return addition.valueNumber;
  }, null);
}

export function shouldShowManagedMarginNotice(
  confirmedMarginPercentageValue: string,
  managedDefaultMarginPercentage: number | null | undefined,
): boolean {
  if (!isFiniteNumber(managedDefaultMarginPercentage)) {
    return false;
  }

  const confirmedMarginPercentage = parseLocalizedNumberInput(confirmedMarginPercentageValue, { allowNegative: true });

  return (
    Number.isFinite(confirmedMarginPercentage) &&
    Math.abs(confirmedMarginPercentage - managedDefaultMarginPercentage) > NUMBER_COMPARISON_EPSILON
  );
}

export function buildVehicleValuationConfirmBody(
  uuid: string,
  values: VehicleValuationConfirmFormValues,
): VehicleValuationConfirmBody & { uuid: string } {
  const isLeasingBuyoutType = values.valuationType === VehicleValuationType.LeasingBuyout;
  const isWholesaleBuyerType = values.valuationType === VehicleValuationType.WholesaleBuyer;
  const parseSubmissionValue = (value: string | undefined, options: LocalizedNumberOptions): number =>
    isLeasingBuyoutType ? 0 : parseLocalizedNumberInput(value ?? "", options);
  const nonNegativeNumberOptions = { allowNegative: false } satisfies LocalizedNumberOptions;
  const signedNumberOptions = { allowNegative: true } satisfies LocalizedNumberOptions;

  return {
    uuid,
    confirmedTradeGross: parseSubmissionValue(values.confirmedTradeGross, nonNegativeNumberOptions),
    confirmedSalesGross: parseSubmissionValue(values.confirmedSalesGross, nonNegativeNumberOptions),
    confirmedWarranty: isWholesaleBuyerType
      ? 0
      : parseSubmissionValue(values.confirmedWarranty, nonNegativeNumberOptions),
    confirmedVehiclePreparationCosts: isWholesaleBuyerType
      ? 0
      : parseSubmissionValue(values.confirmedVehiclePreparationCosts, nonNegativeNumberOptions),
    confirmedMarginGross: isWholesaleBuyerType
      ? 0
      : parseSubmissionValue(values.confirmedMarginGross, signedNumberOptions),
    confirmedMarginPercentage: isWholesaleBuyerType
      ? 0
      : parseSubmissionValue(values.confirmedMarginPercentage, nonNegativeNumberOptions),
    ...(isLeasingBuyoutType
      ? {
          leaseBuyoutAmount: parseLocalizedNumberInput(values.leaseBuyoutAmount ?? "", {
            allowNegative: false,
          }),
        }
      : {}),
    note: values.note?.trim() ? values.note.trim() : null,
    valuationType: values.valuationType,
  };
}
